import { Box } from "@mui/material"
import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import * as Tone from "tone";
import Vex from "vexflow";

interface SheetMusicProps {
    currentNotes?: number[];
    currentMelody?: string[];
    activeNoteIndex?: number;
}

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const getVexNote = (midi: number) => {
    const noteIndex = midi % 12;
    const octave = Math.floor(midi / 12) - 1;
    return `${NOTE_NAMES[noteIndex]}${octave}`;
};

const parseVexNoteToMidi = (vexNote: string): number | null => {
    // This regex finds note names like C#4, Bb3 etc.
    const matches = vexNote.match(/[A-G][#b]?[0-9]/g);
    if (!matches || matches.length === 0) return null;

    // For chords like (C4 E4 G4)/q, it returns ['C4', 'E4', 'G4'].
    // We use the first (and lowest) note to determine the clef.
    try {
        return Tone.Frequency(matches[0]).toMidi();
    } catch (e) { return null; }
}

// Вспомогательная функция для расчета размера такта исходя из длительности мелодии
const calculateTimeSignature = (melody: string[]): string => {
    let totalTicks = 0;
    const TICKS: Record<string, number> = {
        'w': 4096,
        'h': 2048,
        'q': 1024,
        '8': 512,
        '16': 256,
    };

    melody.forEach(m => {
        const parts = m.split('/');
        const dur = parts.find(p => TICKS[p]) || 'q';
        totalTicks += TICKS[dur];
    });

    // Используем 16-е ноты как базу, чтобы всегда получать целое число в числителе
    return `${totalTicks / 256}/16`;
};

export const SheetMusic: React.FC<SheetMusicProps> = ({ currentMelody, currentNotes = [], activeNoteIndex = 0 }) => {
    const scoreRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);

    useLayoutEffect(() => {
        if (scoreRef.current) {
            const measure = () => {
                setWidth(scoreRef.current?.clientWidth || 0);
            }
            measure();
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
    }, []);

    useEffect(() => {
        const { Factory } = Vex.Flow;
        if (!scoreRef.current || width <= 0) return;

        // Очищаем контейнер перед перерисовкой
        scoreRef.current.innerHTML = '';

        // Инициализация VexFlow
        const vf = new Factory({
            renderer: { elementId: scoreRef.current.id, backend: Vex.Flow.Renderer.Backends.SVG, width: width, height: 250 }
        });

        const score = vf.EasyScore();
        const system = vf.System({ width: width > 20 ? width - 20 : width });

        let trebleStr = '';
        let bassStr = '';
        let timeSig = '4/4';

        let wrongKeyIndicesTreble: { noteIndex: number, keyIndex: number }[] = [];
        let wrongKeyIndicesBass: { noteIndex: number, keyIndex: number }[] = [];

        if (currentMelody && currentMelody.length > 0) {
            const trebleMelody: string[] = [];
            const bassMelody: string[] = [];

            currentMelody.forEach((noteString, index) => {
                const duration = noteString.split('/').find(p => ['w', 'h', 'q', '8', '16'].includes(p)) || 'q';
                const isRest = noteString.includes('/r');
                const originalMatches = isRest ? [] : (noteString.match(/[A-G][#b]?[0-9]/g) || []);
                
                let isBass = false;
                if (!isRest && originalMatches.length > 0) {
                    try {
                        const lowestMidi = Tone.Frequency(originalMatches[0]).toMidi();
                        if (lowestMidi < 60) isBass = true;
                    } catch (e) {}
                }
                
                let finalNoteString = noteString;
                
                if (index === activeNoteIndex && currentNotes && currentNotes.length > 0) {
                    const originalMidis = originalMatches.map(n => {
                        try { return Tone.Frequency(n).toMidi(); } catch(e) { return null; }
                    }).filter(n => n !== null) as number[];
                    
                    const uniqueCurrentNotes = Array.from(new Set(currentNotes));
                    const wrongMidis = uniqueCurrentNotes.filter(n => !originalMidis.includes(n));
                    
                    if (wrongMidis.length > 0) {
                        const allNotes = [...originalMidis, ...wrongMidis].map(midi => {
                            const origIdx = originalMidis.indexOf(midi);
                            if (origIdx !== -1) {
                                return { midi, key: originalMatches[origIdx] };
                            } else {
                                return { midi, key: getVexNote(midi) };
                            }
                        }).sort((a, b) => a.midi - b.midi);

                        const allKeys = allNotes.map(n => n.key);
                        finalNoteString = allKeys.length > 1 ? `(${allKeys.join(' ')})/${duration}` : `${allKeys[0]}/${duration}`;
                        
                        if (isRest && allNotes.length > 0) {
                            if (allNotes[0].midi < 60) isBass = true;
                        }

                        wrongMidis.forEach(wm => {
                            const keyIdx = allNotes.findIndex(n => n.midi === wm);
                            if (isBass) {
                                wrongKeyIndicesBass.push({ noteIndex: index, keyIndex: keyIdx });
                            } else {
                                wrongKeyIndicesTreble.push({ noteIndex: index, keyIndex: keyIdx });
                            }
                        });
                    }
                }

                if (isBass) {
                    bassMelody.push(finalNoteString);
                    trebleMelody.push(`B4/${duration}/r`);
                } else {
                    trebleMelody.push(finalNoteString);
                    bassMelody.push(`D3/${duration}/r`);
                }
            });

            trebleStr = trebleMelody.join(', ');
            bassStr = bassMelody.join(', ');
            timeSig = calculateTimeSignature(currentMelody);
        } else {
            const trebleNotes = currentNotes.filter(n => n >= 60).map(getVexNote);
            const bassNotes = currentNotes.filter(n => n < 60).map(getVexNote);

            const createVoice = (notes: string[], defaultRest: string) => {
                if (notes.length === 0) return defaultRest;
                if (notes.length === 1) return `${notes[0]}/w`;
                return `(${notes.join(' ')})/w`;
            };

            trebleStr = createVoice(trebleNotes, 'B4/w/r');
            bassStr = createVoice(bassNotes, 'D3/w/r');
            timeSig = '4/4'; // Целая нота /w соответствует 4/4
        }

        const trebleStaveNotes = score.notes(trebleStr, { stem: 'up' });
        const bassStaveNotes = score.notes(bassStr, { clef: 'bass', stem: 'down' });

        if (currentMelody && currentMelody.length > 0) {
            for (let index = 0; index < currentMelody.length; index++) {
                const trebleNote = trebleStaveNotes[index];
                const bassNote = bassStaveNotes[index];
                
                const noteToStyle = (trebleNote && !trebleNote.isRest()) ? trebleNote : 
                                    ((bassNote && !bassNote.isRest()) ? bassNote : trebleNote);

                if (noteToStyle) {
                    if (index < activeNoteIndex) {
                        noteToStyle.setStyle({ fillStyle: "green", strokeStyle: "green" });
                    } else if (index === activeNoteIndex) {
                        noteToStyle.setStyle({ fillStyle: "orange", strokeStyle: "orange" });
                    }
                }
            }

            wrongKeyIndicesTreble.forEach(w => {
                const note = trebleStaveNotes[w.noteIndex];
                if (note && !note.isRest()) {
                    (note as any).setKeyStyle(w.keyIndex, { fillStyle: "red", strokeStyle: "red" });
                }
            });
            
            wrongKeyIndicesBass.forEach(w => {
                const note = bassStaveNotes[w.noteIndex];
                if (note && !note.isRest()) {
                    (note as any).setKeyStyle(w.keyIndex, { fillStyle: "red", strokeStyle: "red" });
                }
            });
        }

        const trebleVoice = score.voice(trebleStaveNotes, { time: timeSig });
        const bassVoice = score.voice(bassStaveNotes, { time: timeSig });

        system.addStave({
            voices: [trebleVoice],
        }).addClef('treble').addTimeSignature(timeSig);

        system.addStave({
            voices: [bassVoice],
        }).addClef('bass').addTimeSignature(timeSig);

        system.addConnector();

        vf.draw();

        // Делаем SVG адаптивным и ограничиваем высоту
        const svg = scoreRef.current.querySelector('svg');
        if (svg) {
            svg.setAttribute('viewBox', `0 0 ${width} 250`);
            svg.style.width = '100%';
            svg.style.height = 'auto';
            svg.style.maxHeight = '50vh';
        }
    }, [currentNotes, width, currentMelody, activeNoteIndex]);

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, width: '100%', maxHeight: '50vh' }}>
            <div id={'score'} ref={scoreRef} style={{ width: '100%', display: 'flex', justifyContent: 'center' }} />
        </Box>
    )
}
